// Claude API Configuration Component

import React, { useState, useEffect } from 'react';
import { claudeService } from '../../services/ai/claudeService';
import { conversationalAgent } from '../../services/ai/conversationalAgent';
import { Key, Check, AlertCircle, Settings, Eye, EyeOff } from 'lucide-react';

interface ClaudeConfigProps {
  className?: string;
  onConfigChange?: (configured: boolean) => void;
}

export const ClaudeConfig: React.FC<ClaudeConfigProps> = ({
  className = '',
  onConfigChange
}) => {
  const [apiKey, setApiKey] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'success' | 'error'>('unknown');
  const [showKey, setShowKey] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    // Check if API key is already configured
    const existingKey = localStorage.getItem('claude-api-key');
    if (existingKey) {
      setApiKey(existingKey);
      setIsConfigured(true);
      testConnection();
    }
  }, []);

  useEffect(() => {
    onConfigChange?.(isConfigured && connectionStatus === 'success');
  }, [isConfigured, connectionStatus, onConfigChange]);

  const testConnection = async () => {
    setIsTestingConnection(true);
    try {
      const success = await conversationalAgent.testClaudeConnection();
      setConnectionStatus(success ? 'success' : 'error');
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnectionStatus('error');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      setConnectionStatus('error');
      return;
    }

    try {
      claudeService.setApiKey(apiKey.trim());
      setIsConfigured(true);
      await testConnection();
      setShowConfig(false);
    } catch (error) {
      console.error('Failed to save API key:', error);
      setConnectionStatus('error');
    }
  };

  const handleToggleMode = () => {
    const newMode = !conversationalAgent.isClaudeAPIEnabled();
    if (newMode) {
      conversationalAgent.enableClaudeAPI();
    } else {
      conversationalAgent.disableClaudeAPI();
    }

    if (!newMode) {
      setConnectionStatus('unknown');
    }
  };

  const getStatusIcon = () => {
    if (isTestingConnection) {
      return <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
    }

    switch (connectionStatus) {
      case 'success':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Key className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    if (!conversationalAgent.isClaudeAPIEnabled()) {
      return 'Using rule-based responses';
    }

    if (isTestingConnection) {
      return 'Testing connection...';
    }

    switch (connectionStatus) {
      case 'success':
        return 'Claude API connected';
      case 'error':
        return 'Connection failed';
      default:
        return 'Not configured';
    }
  };

  const getStatusColor = () => {
    if (!conversationalAgent.isClaudeAPIEnabled()) {
      return 'text-yellow-600 dark:text-yellow-400';
    }

    switch (connectionStatus) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (!showConfig) {
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <span className={`text-sm font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>

        <button
          onClick={() => setShowConfig(true)}
          className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded"
          title="Configure Claude API"
        >
          <Settings className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Claude API Configuration
            </h3>
            <button
              onClick={() => setShowConfig(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            {/* AI Mode Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Response Mode
              </label>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleToggleMode}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    conversationalAgent.isClaudeAPIEnabled() ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      conversationalAgent.isClaudeAPIEnabled() ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {conversationalAgent.isClaudeAPIEnabled() ? 'Claude API' : 'AI Disabled'}
                </span>
              </div>
            </div>

            {/* API Key Input */}
            {conversationalAgent.isClaudeAPIEnabled() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Claude API Key
                </label>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-ant-api03-..."
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showKey ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Get your API key from{' '}
                  <a
                    href="https://console.anthropic.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    console.anthropic.com
                  </a>
                </p>
              </div>
            )}

            {/* Connection Status */}
            {conversationalAgent.isClaudeAPIEnabled() && (
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-sm text-gray-700 dark:text-gray-300">Connection Status:</span>
                <div className="flex items-center space-x-2">
                  {getStatusIcon()}
                  <span className={`text-sm ${getStatusColor()}`}>
                    {getStatusText()}
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowConfig(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white py-2 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              {conversationalAgent.isClaudeAPIEnabled() ? (
                <button
                  onClick={handleSaveApiKey}
                  disabled={!apiKey.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Save & Test
                </button>
              ) : (
                <button
                  onClick={() => setShowConfig(false)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};